import { useMemo } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { METHOD, sendJSONRequest } from '@/state/http-request.helpers.ts';
import { GET_USER_QUERY_KEY } from '@/state/queries/users/use-get-user';
import { getCatalogDisplayFeatureFlagNames } from '@/constants/product-catalog.ts';
import { buildApiUrl } from '@/utils/build-api-url.ts';

interface CheckFeatureAccessResponse {
  hasAccess: boolean;
}

export async function checkFeatureAccessRequest(
  featureName: string,
  token: string | null | undefined,
): Promise<CheckFeatureAccessResponse> {
  const url = buildApiUrl(`features/${featureName}/check-access`);
  return sendJSONRequest({
    url,
    method: METHOD.GET,
    token: token || undefined,
  }) as Promise<CheckFeatureAccessResponse>;
}

interface UserResponse {
  globalRoles?: string[];
}

/**
 * Hook to check if the current user has access to a feature flag.
 * Uses backend endpoint to ensure A/B testing logic matches exactly.
 *
 * @param featureName - The kebab-case name of the feature (e.g., "advanced-org-features")
 * @returns Object with `hasAccess` boolean and `isLoading` boolean
 *
 * @example
 * ```tsx
 * const { hasAccess, isLoading } = useFeatureAccess('advanced-org-features');
 *
 * if (isLoading) return <Loading />;
 * if (hasAccess) {
 *   return <NewFeatureComponent />;
 * }
 * ```
 */
export function useFeatureAccess(featureName: string) {
  const { getToken, isLoaded, isSignedIn } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ['check-feature-access', featureName],
    enabled: isLoaded && Boolean(isSignedIn),
    queryFn: async (): Promise<CheckFeatureAccessResponse> => {
      const token = await getToken();
      return checkFeatureAccessRequest(featureName, token);
    },
    retry: false, // Don't retry on 404/403 errors
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  return {
    hasAccess: data?.hasAccess ?? false,
    // Treat auth warmup as loading so callers don't briefly treat "unsigned" as "no access".
    isLoading: !isLoaded || (Boolean(isSignedIn) && isLoading),
  };
}

/**
 * Resolves `displayFeatureFlag` access for all event catalog SKUs in one batched set of queries
 * (same cache keys as `useFeatureAccess`).
 */
export function useCatalogDisplayFeatureAccess() {
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const flagNames = useMemo(() => getCatalogDisplayFeatureFlagNames(), []);
  const authReady = isLoaded && Boolean(isSignedIn);

  const results = useQueries({
    queries: flagNames.map((featureName) => ({
      queryKey: ['check-feature-access', featureName] as const,
      enabled: authReady,
      queryFn: async (): Promise<CheckFeatureAccessResponse> => {
        const token = await getToken();
        return checkFeatureAccessRequest(featureName, token);
      },
      retry: false,
      staleTime: 5 * 60 * 1000,
    })),
  });

  const isLoading = !isLoaded || (authReady && flagNames.length > 0 && results.some((r) => r.isLoading));

  const featureAccess: Record<string, boolean> = {};
  flagNames.forEach((name, i) => {
    featureAccess[name] = results[i]?.data?.hasAccess ?? false;
  });

  return { featureAccess, isLoading };
}

/**
 * Hook to check if the current user has any of the specified roles
 *
 * @param roles - Array of role names to check (e.g., ['beta_user', 'admin'])
 * @returns Object with `hasRole` boolean and `isLoading` boolean
 *
 * @example
 * ```tsx
 * const { hasRole } = useHasRole(['beta_user', 'admin']);
 * if (hasRole) {
 *   return <BetaFeature />;
 * }
 * ```
 */
export function useHasRole(roles: string[]) {
  const { getToken, isLoaded, isSignedIn } = useAuth();

  const { data: user, isLoading } = useQuery({
    queryKey: [GET_USER_QUERY_KEY],
    enabled: isLoaded && Boolean(isSignedIn),
    queryFn: async (): Promise<UserResponse> => {
      const token = await getToken();
      const url = `${import.meta.env.VITE_API_URL}/users`;
      return sendJSONRequest({ url, method: METHOD.GET, token: token || undefined }) as Promise<UserResponse>;
    },
  });

  const hasRole = useMemo(() => {
    if (isLoading || !user) {
      return false;
    }

    const userRoles = user.globalRoles || [];
    return roles.some((role) => userRoles.includes(role));
  }, [user, roles, isLoading]);

  return {
    hasRole,
    isLoading,
  };
}
